import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCustomerApprovals } from "@/hooks/useCustomerPortal";
import { useIsMobile } from "@/hooks/use-mobile";
import { ClipboardCheck } from "lucide-react";

const statusColor = (s: string) => {
  switch (s) {
    case "approved": return "bg-green-500/10 text-green-700 border-green-200";
    case "rejected": return "bg-destructive/10 text-destructive border-destructive/20";
    case "pending": return "bg-yellow-500/10 text-yellow-700 border-yellow-200";
    default: return "";
  }
};

const CustomerApprovals = () => {
  const { data: approvals, isLoading } = useCustomerApprovals();
  const isMobile = useIsMobile();

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  }

  const items = approvals ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Approvals</h1>
        <p className="text-muted-foreground text-sm">{items.length} approval item{items.length !== 1 ? "s" : ""}</p>
      </div>

      {items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><ClipboardCheck className="mx-auto h-10 w-10 mb-2 opacity-40" /><p>No approval items available</p></CardContent></Card>
      ) : isMobile ? (
        <div className="space-y-3">
          {items.map((a) => (
            <Card key={a.id}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{a.title}</span>
                  <Badge variant="outline" className={`capitalize text-xs ${statusColor(a.status)}`}>{a.status}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span className="capitalize">{a.request_type.replace(/_/g, " ")}</span>
                  <span>{a.projects?.name || "—"}</span>
                </div>
                <p className="text-xs text-muted-foreground">{a.created_at?.split("T")[0]}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.title}</TableCell>
                    <TableCell className="text-muted-foreground capitalize">{a.request_type.replace(/_/g, " ")}</TableCell>
                    <TableCell className="text-muted-foreground">{a.projects?.name || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className={`capitalize text-xs ${statusColor(a.status)}`}>{a.status}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{a.created_at?.split("T")[0]}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CustomerApprovals;
